<template>
  <div class="form-container card shadow-sm p-3">
    <!-- Cabeçalho -->
    <div class="header d-flex justify-content-between align-items-center">
      <h4 class="form-title">{{ usuario.id ? "Editar Usuário" : "Novo Usuário" }}</h4>
      <button class="btn btn-secondary" @click="voltar">Voltar</button>
    </div>

    <!-- Espaçador -->
    <div class="spacer"></div>

    <div v-if="errors.backend" class="alert alert-danger" role="alert">{{ errors.backend }}</div>

    <!-- Formulário -->
    <BaseForm 
      :isFormValid="isFormValid"
      :isSubmitting="isSubmitting"
      @submit="salvarUsuario"
      @cancel="voltar"
    >
      <div class="form-section">
        <h5 class="section-title">Dados do Usuário</h5>

        <!-- Pessoa Física -->
        <div class="row">
          <div class="col-md-6">
            <div class="form-group">
              <label for="pessoa-fisica" class="form-label">
                Pessoa <span class="required">*</span>
              </label>
              <div class="d-flex">
                <input
                  id="pessoa-fisica"
                  type="text"
                  class="form-control pessoa-input disabled-input"
                  :value="usuario.pessoa_fisica_nome || ''"
                  disabled
                />
                <button
                  class="btn btn-primary pessoa-icon-btn"
                  type="button"
                  @click="abrirModal"
                >
                  <i class="bi bi-person"></i>
                </button>
              </div>
              <span v-if="formSubmitted && errors.pessoa_fisica_id" class="error-message">
                {{ errors.pessoa_fisica_id }}
              </span>
            </div>
          </div>
        
          <!-- Email -->
          <div class="col-md-5">
            <div class="form-group">
              <label for="email">Email <span class="required">*</span></label>
              <input
                id="email"
                type="email"
                v-model="usuario.email"
                class="form-control"
                :class="{ 'error-input': formSubmitted && errors.email }"
                placeholder="Digite o email"
              />
              <span v-if="formSubmitted && errors.email" class="error-message">
                {{ errors.email }}
              </span>
            </div>
          </div>
        </div>
        <div v-if="criacao" class="row">
          <div class="col-md-6 form-group">
            <label for="nova-senha">Senha <span class="required">*</span></label>
            <input id="nova-senha" v-model="senha" type="password" autocomplete="new-password"
              required class="form-control" aria-describedby="regra-senha" />
            <small id="regra-senha">Ao menos 8 caracteres e no máximo 72 bytes UTF-8.</small>
            <span v-if="senha && erroSenha" class="error-message">{{ erroSenha }}</span>
          </div>
          <div class="col-md-6 form-group">
            <label for="confirmar-senha">Confirmar senha <span class="required">*</span></label>
            <input id="confirmar-senha" v-model="confirmacaoSenha" type="password"
              autocomplete="new-password" required class="form-control" />
            <span v-if="confirmacaoSenha && senha !== confirmacaoSenha" class="error-message">As senhas não conferem.</span>
          </div>
        </div>
        <div class="row">
          <!-- Perfil -->
          <div class="col-md-6">
            <div class="form-group">
              <label for="perfil">Perfil <span class="required">*</span></label>
              <select
                id="perfil"
                v-model="usuario.profile_id"
                class="form-control"
              >
                <option disabled value="">Selecione uma opção</option>
                <option v-for="perfil in perfis" :key="perfil.id" :value="perfil.id">
                  {{ perfil.nome }}
                </option>
              </select>
              <span v-if="formSubmitted && errors.profile_id" class="error-message">
                {{ errors.profile_id }}
              </span>
            </div>
          </div>

          <!-- Status -->
          <div class="col-md-4">
            <div class="form-group">
              <label for="status">Status <span class="required">*</span></label>
              <div class="form-switch d-flex align-items-center">
                <input
                  type="checkbox"
                  id="status"
                  v-model="usuario.is_active"
                  class="form-check-input"
                />
                <label for="status" class="form-check-label ms-2">
                  {{ usuario.is_active ? "Ativo" : "Inativo" }}
                </label>
              </div>
            </div>
          </div>
        </div>
      </div>
    </BaseForm>

    <!-- Modal de Seleção de Pessoa Física -->
    <CarregarPessoaFisica 
      :mostrar="showModal" 
      @fechar="fecharModal" 
      @selecionar="selecionarPessoa"
    />
  </div>
</template>

<script>
import { reactive, ref, computed, onMounted, onBeforeUnmount, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import axios from "@/services/axios";
import BaseForm from "@/components/Layout/BaseForm.vue";
import CarregarPessoaFisica from "@/components/modals/CarregarPessoaFisica.vue";

export default {
  components: { BaseForm, CarregarPessoaFisica },
  setup() {
    const router = useRouter();
    const route = useRoute();

    const usuario = reactive({
      email: "",
      profile_id: "",
      is_active: true,
      pessoa_fisica_id: "",
      pessoa_fisica_nome: "",
    });

    const senha = ref('');
    const confirmacaoSenha = ref('');
    const criacao = computed(() => !route.params.id && !usuario.id);
    const erroSenha = computed(() => {
      if (senha.value.trim().length < 8 || new TextEncoder().encode(senha.value).length > 72) {
        return 'Informe uma senha com ao menos 8 caracteres e no máximo 72 bytes.';
      }
      return '';
    });
    const limparSenha = () => { senha.value = ''; confirmacaoSenha.value = ''; };
    onBeforeUnmount(limparSenha);
    watch(() => route.fullPath, limparSenha);
    watch(() => route.params.id, (id) => {
      limparSenha();
      for (const key of Object.keys(usuario)) delete usuario[key];
      Object.assign(usuario, { email: '', profile_id: '', is_active: true,
        pessoa_fisica_id: '', pessoa_fisica_nome: '' });
      if (id) carregarUsuario(id);
    });

    const errors = reactive({});
    const formSubmitted = ref(false);
    const isSubmitting = ref(false);
    const perfis = ref([]);

    // Controle do modal
    const showModal = ref(false);

    const isFormValid = computed(() => {
      return !!usuario.email && !!usuario.profile_id && !!usuario.pessoa_fisica_id &&
        (criacao.value ? !erroSenha.value && senha.value === confirmacaoSenha.value : !!usuario.id);
    });

    const voltar = () => {
      limparSenha();
      router.push("/configuracoes/usuarios");
    };

    const carregarPerfis = async () => {
      try {
        // 1ª tentativa: sem paginação
        let resp;
        try {
          resp = await axios.get('/configuracoes/perfis-de-usuarios', {
            params: { limit: 'all', sortField: 'nome', sortOrder: 'asc' }
          });
        } catch {
          // Fallback: paginação "alta"
          resp = await axios.get('/configuracoes/perfis-de-usuarios', {
            params: { page: 1, limit: 10000, sortField: 'nome', sortOrder: 'asc' }
          });
        }

        const lista = Array.isArray(resp?.data?.data)
          ? resp.data.data
          : (Array.isArray(resp?.data) ? resp.data : []);

        const somenteAtivos = (lista || []).filter(p => p.ativo !== false);

        somenteAtivos.sort((a, b) =>
          String(a.nome ?? a.descricao ?? '')
            .localeCompare(String(b.nome ?? b.descricao ?? ''), 'pt-BR', {
              sensitivity: 'base',
              numeric: true
            })
        );

        perfis.value = somenteAtivos;
      } catch (error) {
        console.error('Erro ao carregar perfis.');
        perfis.value = [];
      }
    };

    const abrirModal = () => {
      showModal.value = true;
    };

    const fecharModal = () => {
      showModal.value = false;
    };

    const selecionarPessoa = ({ pessoa }) => {
      usuario.pessoa_fisica_id = pessoa.id;
      usuario.pessoa_fisica_nome = pessoa.nome;
      usuario.email = pessoa.email;
      fecharModal();
    };

    const carregarUsuario = async (id) => {
      try {
        const response = await axios.get(
          `/configuracoes/usuarios/${id}`
        );
        if (String(route.params.id) !== String(id)) return;
        // Nunca guardar credenciais devolvidas por endpoints legados no formulário.
        for (const key of ['id', 'pessoa_fisica_id', 'pessoa_fisica_nome', 'email', 'profile_id', 'is_active', 'avatar_url']) {
          usuario[key] = response.data[key];
        }
      } catch (error) {
        console.error("Erro ao carregar dados do usuário.");
      }
    };

    const salvarUsuario = async () => {
      if (isSubmitting.value) return;
      formSubmitted.value = true;
      errors.backend = '';
      isSubmitting.value = true;

      if (!isFormValid.value) {
        isSubmitting.value = false;
        errors.backend = criacao.value && erroSenha.value ? erroSenha.value : 'Confira os campos obrigatórios e a confirmação de senha.';
        return;
      }

      try {
        const url = usuario.id
          ? `/configuracoes/usuarios/${usuario.id}`
          : `/configuracoes/usuarios`;
        const method = usuario.id ? "put" : "post";

        const payload = {
          pessoa_fisica_id: usuario.pessoa_fisica_id, email: usuario.email,
          profile_id: usuario.profile_id, is_active: usuario.is_active, avatar_url: usuario.avatar_url,
        };
        if (criacao.value) payload.password = senha.value;
        await axios[method](url, payload);
        limparSenha();
        router.push("/configuracoes/usuarios");
      } catch (error) {
        const status = error.response?.status;
        errors.backend = status === 401 ? 'Sessão expirada. Entre novamente.'
          : status === 403 ? 'Você não tem permissão para salvar este usuário.'
          : status === 400 ? 'Não foi possível salvar. Confira os dados informados.'
          : 'Erro ao salvar o usuário. Tente novamente.';
      } finally {
        limparSenha();
        isSubmitting.value = false;
      }
    };

    const carregarNovaPessoa = async (newPersonId) => {
      try {
        const response = await axios.get(`/cadastros/pessoas-fisicas/${newPersonId}`);
        const novaPessoa = response.data;
        usuario.pessoa_fisica_id = novaPessoa.id;
        usuario.pessoa_fisica_nome = novaPessoa.nome;
        usuario.email = novaPessoa.email;  // Atribui o e-mail automaticamente
      } catch (error) {
        console.error("Erro ao carregar nova pessoa.");
      } finally {
        // Remove o newPersonId da query para evitar recarregamento repetido
        router.replace({ query: { ...route.query, newPersonId: undefined } });
      }
    };

    onMounted(() => {
      carregarPerfis();
      const usuarioId = route.params.id;
      if (usuarioId) {
        carregarUsuario(usuarioId);
      }
      // Se newPersonId estiver na query, carrega a nova pessoa
      if (route.query.newPersonId) {
        carregarNovaPessoa(route.query.newPersonId);
      }
    });

    return {
      usuario,
      senha,
      confirmacaoSenha,
      criacao,
      erroSenha,
      errors,
      formSubmitted,
      isFormValid,
      isSubmitting,
      perfis,
      showModal,
      abrirModal,
      fecharModal,
      selecionarPessoa,
      salvarUsuario,
      voltar,
    };
  },
};
</script>

<style scoped>
.form-container {
  margin: 20px;
}
.form-section {
  background: white;
  padding: 15px;
  border-radius: 8px;
  border: 1px solid #dee2e6;
  margin-bottom: 1rem;
}
.section-title {
  font-size: 0.9rem;
  font-weight: bold;
  margin-bottom: 10px;
}
.disabled-input {
  background: #e9ecef;
  border: 1px solid #ced4da;
}
.required {
  color: red;
}
.pessoa-input {
  flex: 1;
}
.add-turma-container {
  display: flex;
  justify-content: flex-end;
  margin-top: 5px;
}
.form-actions {
  margin-top: 20px;
}

.spacer {
  margin-bottom: 20px;
}

</style>
